import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {Exhibit, ExhibitData} from "types/Exhibit";
import Button from "components/Button";
import {ExclamationCircleIcon, EyeIcon, PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DeleteExhibit from "pages/Exhibit/Delete";
import Gallery from "components/Gallery";
import Icon from 'components/Icon';
import {formattedDate, secondsToHuman} from 'services/date';
import Chip from "components/Chip";
import BooleanIndicator from "components/BooleanIndicator";
import {useOperator} from "context/OperatorContext";
import Empty from "components/Empty";
import {
  ClockIcon,
  ExternalLinkIcon,
  EyeOffIcon,
  IdentificationIcon,
  InboxIcon,
  PresentationChartLineIcon,
  PuzzleIcon,
  QuestionMarkCircleIcon,
  ThumbDownIcon,
  ThumbUpIcon,
  VideoCameraIcon
} from "@heroicons/react/outline";
import IconBadge from "components/IconBadge";
import IconName from "types/IconName";
import {toTitleCase} from "services/string";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import {invalidate} from "../../../services/query-client";
import {useNotifications} from "../../../context/NotificationContext";

// Types ----

type ExhibitUpdate = {
  id: number
  data: ExhibitData
}

// Helpers ----

function toPercentage(rate?: number): string {
  return (100 * Number(rate)).toFixed(1) + '%';
}

// Component ----

function ViewExhibit() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const navigate = useNavigate();
  const {notify} = useNotifications();
  const {id} = useParams();

  const [deletingExhibit, setDeletingExhibit] = useState<Exhibit | null>(null);

  const {data: exhibit, isLoading} = useQuery(
    ['exhibit', {id}],
    () => ngage.exhibits.get(Number(id)),
    {enabled: auth.can('exhibits:read')}
  )

  const {mutateAsync: updateExhibit, isLoading: isUpdating} = useMutation(
    (update: ExhibitUpdate) => ngage.exhibits.update(update.id, update.data)
  )

  // Prepare action handlers
  function deleteExhibit() {
    if (exhibit) setDeletingExhibit(exhibit)
  }

  function editExhibit() {
    if (id) navigate(`/exhibits/${id}/edit`)
  }

  async function publishExhibit() {
    if (!exhibit) return;

    const publishing = !exhibit.is_published

    try {
      await updateExhibit({id: exhibit.id, data: {is_published: publishing}})
      void invalidate('exhibits')
      void invalidate('exhibit')

      if (publishing) {
        notify("success", `${termFor('Exhibit')} Published`, `${exhibit.name} has been published`)
      } else {
        notify("success", `${termFor('Exhibit')} Unpublished`, `${exhibit.name} has been unpublished`)
      }
    } catch (error: any) {
      notify("error", "Something Went Wrong", `${exhibit.name} published status could not be changed`)
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('exhibits:read')) {
    return <NotAuthorised/>
  }

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('exhibits:write') &&
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={deleteExhibit}>Delete</Button>
      }

      {(auth.can('exhibits:write') || auth.can('exhibits:publish')) &&
        <Button
          color={Button.colors.WHITE}
          icon={exhibit?.is_published ? EyeOffIcon : EyeIcon}
          onClick={publishExhibit}
          showSpinner={isUpdating}
          disabled={!exhibit}
        >
          {`${exhibit?.is_published ? 'Hide' : 'Publish'}`}
        </Button>
      }

      {auth.can('exhibits:write') &&
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={editExhibit}>Edit</Button>
      }
    </>
  )

  const breadcrumbs = {
    "Content": "/",
    [termFor('Exhibits')]: "/exhibits",
    [exhibit?.name || `View ${termFor('Exhibit')}`]: `/exhibits/${id}`
  }

  return (
    <Page
      title={exhibit?.name || `View ${termFor('Exhibit')}`}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >
      {!!exhibit &&
        <>
          <section>
            <StatList>
              <StatBox
                name={'Positive Feedback'}
                icon={ThumbUpIcon}
                stat={exhibit.aggregated_feedback?.count ? toPercentage(exhibit.aggregated_feedback.positive_rate) : 'N/A'}
              />

              <StatBox
                name={'Negative Feedback'}
                icon={ThumbDownIcon}
                stat={exhibit.aggregated_feedback?.count ? toPercentage(exhibit.aggregated_feedback.negative_rate) : 'N/A'}
              />

              <StatBox
                name={'Feedback'}
                icon={InboxIcon}
                stat={exhibit.aggregated_feedback?.count.toLocaleString()}
              />

              <StatBox
                name={'Feedback Trend'}
                icon={PresentationChartLineIcon}
                stat={toTitleCase(exhibit.aggregated_feedback?.category_trend ?? 'neutral')}
              />

              <StatBox
                name={termFor('Sponsorships')}
                icon={IdentificationIcon}
                stat={exhibit.sponsorships.length.toLocaleString()}
              />

              <StatBox
                name={'Avg. Dwell Duration'}
                icon={ClockIcon}
                stat={exhibit.average_dwell_duration ? secondsToHuman(exhibit.average_dwell_duration) : 'N/A'}
                tooltip={`This ${termFor('exhibit')} must be associated with a beacon in order to collect data.`}
              />
            </StatList>
          </section>

          {/* Exhibit Information */}
          <section className="mt-4">
            <Card
              heading={`${termFor('Exhibit')} Information`}
              subheading={`${exhibit.caption ? exhibit.caption + ' |' : ''} Added on ${formattedDate(exhibit.created_at)}`}
            >
              <DescriptionList>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>{termFor('Exhibit Type')}</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{exhibit.type.name}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Published</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={exhibit.is_published}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Visible</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={!exhibit.is_hidden}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Location</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{exhibit.zone.name} ({exhibit.zone.site.name})</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Mapped</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={exhibit.is_mapped}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Directions</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={exhibit.is_destination}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Description</DescriptionTerm>
                  <DescriptionDetails>
                    <p className="whitespace-pre-line">{exhibit.description}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Accessibility</DescriptionTerm>
                  <DescriptionDetails>
                    {!!exhibit.accessibility_criteria.length && Object.values(exhibit.accessibility_criteria).map(criterion => (
                      <IconBadge icon={IconName.HAND} text={toTitleCase(criterion)} color={IconBadge.colors.BRAND}/>
                    ))}

                    {!exhibit.accessibility_criteria.length &&
                      <p>No criteria listed</p>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Sustainable</DescriptionTerm>
                  <DescriptionDetails>
                    <BooleanIndicator value={exhibit.is_sustainable}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>{termFor('Collections')}</DescriptionTerm>
                  <DescriptionDetails>
                    {exhibit.collections.length ?
                      exhibit.collections.map(collection => collection.name).join(', ') : 'None'
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                {exhibit.link &&
                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Link</DescriptionTerm>
                    <DescriptionDetails>
                      <Chip
                        name={exhibit.link.text}
                        href={exhibit.link.url}
                        description={exhibit.link.url}
                        icon={ExternalLinkIcon}
                      />
                    </DescriptionDetails>
                  </DescriptionListItem>
                }

                {exhibit.stream &&
                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Stream</DescriptionTerm>
                    <DescriptionDetails>
                      <Chip
                        name={toTitleCase(exhibit.stream.provider)}
                        href={exhibit.stream.view_url}
                        description={`${exhibit.stream.reference} (${exhibit.stream.is_online? 'Online' : 'Offline'})`}
                        icon={VideoCameraIcon}
                      />
                    </DescriptionDetails>
                  </DescriptionListItem>
                }
              </DescriptionList>
            </Card>
          </section>

          {/* Facts */}
          <section className="mt-4">
            <Card heading={termFor('Facts')}>
              {exhibit.facts.length < 1 &&
                <Empty
                  icon={QuestionMarkCircleIcon}
                  text="No Facts"
                  hint="Add facts to this exhibit to see them here."
                />
              }

              {exhibit.facts.length > 0 &&
                <DescriptionList>
                  {exhibit.facts.map((fact, index) => (
                    <DescriptionListItem span={1} key={index}>
                      <DescriptionTerm>
                        <Icon name={fact.icon} className="inline h-5 w-5 align-bottom mr-1"/>
                        {fact.name}
                      </DescriptionTerm>
                      <DescriptionDetails>{fact.value}</DescriptionDetails>
                    </DescriptionListItem>
                  ))}
                </DescriptionList>
              }
            </Card>
          </section>

          {/* Detailed Information */}
          {!!exhibit.information.length &&
            <section className="mt-4">
              <Card heading="Detailed Information">
                {exhibit.information.map((section, index) => (
                  <article key={index} className="mb-5">
                    <h3 className="text-base leading-6 font-medium text-gray-900 mb-2">{section.name}</h3>
                    <p className="text-sm whitespace-pre-line">{section.content}</p>
                  </article>
                ))}
              </Card>
            </section>
          }

          {/* Media */}
          {!!exhibit.media.length &&
            <section className="mt-4">
              <Card heading="Media">
                <Gallery media={exhibit.media}/>
              </Card>
            </section>
          }

          {/* Elements */}
          <section className="mt-4">
            <Card heading="Elements">
              {exhibit.elements.length ? (
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exhibit.elements.map(element => (
                    <li key={element.id}>
                      <Chip
                        name={element.name}
                        description={element.caption || element.description}
                        backgroundSrc={element.image?.preview_url}
                        href={`/exhibits/${id}`}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <Empty
                  icon={PuzzleIcon}
                  text={`No ${termFor('Elements')}`}
                  hint={`Add ${termFor('elements')} to this ${termFor('exhibit')} to see them here.`}
                />
              )}
            </Card>
          </section>

          {/* Quizzes */}
          {!!exhibit.quizzes.length &&
            <section className="mt-4">
              <Card heading="Quizzes">
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exhibit.quizzes.map((quiz, index) => (
                    <li key={index}>
                      <Chip
                        name={quiz.title}
                        description={`${quiz.question_count} questions` + (quiz.description ? ` | ${quiz.description}` : '')}
                        href={`/quizzes/${quiz.id}`}
                        backgroundSrc={quiz.image?.preview_url}
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          }

          {/* Notes */}
          {!!exhibit.notes.length &&
            <section className="mt-4">
              <Card heading="Notes">
                <ul className="space-y-8">
                  {exhibit.notes.map((note, index) => {
                    return (
                      <li key={index}>
                        <div className="flex space-x-3">
                          <div className="flex-shrink-0">
                            <img className="h-10 w-10 rounded-full"
                                 src={note.author.profile_photo_url}
                                 alt={note.author.name}/>
                          </div>
                          <div>
                            <div className="text-sm">
                              <span className="font-medium text-gray-500">
                                  {note.author.name}
                              </span>
                            </div>
                            <div className="mt-2 text-sm text-gray-700">
                              <h3 className="text-sm leading-6 font-medium text-gray-900 mb-1">{note.title}</h3>
                              <p className="whitespace-pre-line">{note.body}</p>
                            </div>
                            <div className="mt-2 text-sm flex items-center space-x-2">
                              {note.is_important ?
                                <ExclamationCircleIcon className="text-red-500 h-5 w-5"/>
                                : null}

                              <span className="text-gray-500 font-medium">
                                  {note.created_at?.toRelative()}
                              </span>

                              {note.expires_at ?
                                <>
                                  <span className="text-gray-500 font-medium">·</span>
                                  <span className="text-gray-900 font-medium">
                                       {(note.is_expired ? 'Expired' : 'Expires') + ' ' + note.expires_at.toRelative()}
                                  </span>
                                </>
                                : null}
                            </div>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </Card>
            </section>
          }
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteExhibit
        show={exhibit === deletingExhibit}
        exhibit={deletingExhibit}
        onCancel={() => setDeletingExhibit(null)}
        onDelete={() => navigate('/exhibits')}
        onDeleteFail={() => setDeletingExhibit(null)}
      />
    </Page>
  );
}

export default ViewExhibit;